/**
 * Query param the customer storefront reads on localhost / Vercel preview
 * (see shoporaa_storefront `tenant.ts` + `mobileAppQrPayload.ts`).
 */
export const STOREFRONT_TENANT_QUERY = 'store'

function isSingleOriginPreviewHost(hostname: string): boolean {
  const h = hostname.trim().toLowerCase()
  return h === 'localhost' || h === '127.0.0.1' || h === '::1' || h.endsWith('.vercel.app')
}

function normalizeStorefrontUrl(url: string): string {
  return url.replace(/\/+(?=[?#]|$)/, '')
}

/**
 * Build customer storefront home URL for a store subdomain from the API.
 *
 * `VITE_PUBLIC_STOREFRONT_URL` is the **base pattern**, not a fixed shop URL:
 * - `https://{subdomain}.shopora.com` — replace placeholder with the store slug
 * - `https://shopora.com` — prepend slug (`acme.shopora.com`)
 * - `http://localhost:5174` — same origin; append `?store=<slug>` for dynamic tenant
 */
export function buildStorefrontUrl(subdomain: string, path?: string): string | null {
  const slug = String(subdomain || '').trim().toLowerCase()
  if (!slug) return null
  const template = import.meta.env.VITE_PUBLIC_STOREFRONT_URL?.trim()
  if (!template) return null

  let base: string

  if (template.includes('{subdomain}')) {
    base = template.replace(/\{subdomain\}/gi, encodeURIComponent(slug))
  } else {
    try {
      const u = new URL(template)
      if (isSingleOriginPreviewHost(u.hostname)) {
        u.searchParams.set(STOREFRONT_TENANT_QUERY, slug)
        base = u.toString()
      } else {
        const hostParts = u.hostname.split('.').filter(Boolean)
        if (hostParts.length >= 3) {
          hostParts[0] = slug
          u.hostname = hostParts.join('.')
        } else if (hostParts.length === 2) {
          u.hostname = `${slug}.${u.hostname}`
        } else {
          u.hostname = `${slug}.${u.hostname}`
        }
        u.search = ''
        base = u.toString()
      }
    } catch {
      return null
    }
  }

  base = normalizeStorefrontUrl(base.replace(/\/$/, ''))
  if (!path) return base

  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  try {
    const u = new URL(base)
    u.pathname = normalizedPath
    return normalizeStorefrontUrl(u.toString())
  } catch {
    return normalizeStorefrontUrl(`${base}${normalizedPath}`)
  }
}

export type StorefrontUrlIssue = 'missing_subdomain' | 'missing_env'

export function getStorefrontUrlIssue(subdomain: string | null | undefined): StorefrontUrlIssue | null {
  const slug = String(subdomain ?? '').trim()
  if (!slug) return 'missing_subdomain'
  if (!import.meta.env.VITE_PUBLIC_STOREFRONT_URL?.trim()) return 'missing_env'
  return null
}
